import { Injectable, signal, computed, inject } from '@angular/core';
import { Observable, map, switchMap, catchError, of } from 'rxjs';
import { Balance, WalletTopUp } from '../models';
import { AgencyApiService, BeChargeTransactionResponse, BeTransactionResponse } from './api/agency-api.service';

/** Reservation-currency / wallet-balance context (feature: hotel-reservation-currency).
 *  The header selector, every price surface and the booking call all read `currency()` — it is
 *  the RESERVATION currency: search/offer/checkout re-price in it and the wallet debit happens
 *  in it (README §1). Persisted per SESSION (sessionStorage key 'hbh_ccy'), mirroring how the
 *  search criteria persist — a pre-feature localStorage value is migrated once, read-only. */
const CCY_STORAGE_KEY = 'hbh_ccy';

function readStoredCurrency(): string | null {
  try { return sessionStorage.getItem(CCY_STORAGE_KEY) ?? localStorage.getItem(CCY_STORAGE_KEY); }
  catch { return null; } // storage disabled — in-memory selection still works for this session
}

@Injectable({ providedIn: 'root' })
export class WalletService {
  private agencyApi = inject(AgencyApiService);

  readonly balances = signal<Balance[]>([]);
  readonly currency = signal<string>(readStoredCurrency() || 'EUR');
  readonly topUps = signal<WalletTopUp[]>([]);

  /** Only wallets holding a balance > 0 are offered as reservation currency (README §1) — the
   *  agency is never offered a currency it cannot pay from. A zero-balance wallet still exists
   *  in `balances()` for the Wallet page; it just can't be booked from. */
  readonly payable = computed<Balance[]>(() => this.balances().filter(b => b.amount > 0));

  // ── Reservation-currency switch feedback / programmatic picker state (README §1) ──
  /** Increments on every currency switch — drives the one-shot amber flash on converted amounts. */
  readonly repriceKey = signal(0);
  /** The just-selected code for ~1.6s after a switch — drives the transient "Prices updated to X"
   *  pill in the top bar and the CurrencyPill's "Repriced to X" confirmation. */
  readonly switching = signal<string | null>(null);
  /** Selector popover open state — a signal (not template-local state) so §3/§4 escape hatches
   *  can open it programmatically from any screen via openPicker(). */
  readonly pickerOpen = signal(false);
  /** Reason banner shown at the top of the popover when opened from an error state (§3/§4),
   *  e.g. "Pick a currency with at least SAR 3,450.00 available." — null = no banner. */
  readonly pickerHint = signal<string | null>(null);
  /** Increments when the selector is opened programmatically — pulses the trigger button. */
  readonly pulseKey = signal(0);
  private switchTimer: ReturnType<typeof setTimeout> | null = null;

  openPicker(hint?: string): void {
    this.pickerHint.set(hint ?? null);
    this.pickerOpen.set(true);
    this.pulseKey.update(k => k + 1);
  }
  closePicker(): void {
    this.pickerOpen.set(false);
    this.pickerHint.set(null);
  }
  togglePicker(): void {
    if (this.pickerOpen()) this.closePicker(); else { this.pickerHint.set(null); this.pickerOpen.set(true); }
  }

  /** Balance check for one amount in the CURRENT reservation currency (README §3).
   *  `available` = balance + real credit. The BE has no credit/plafond concept for these agency
   *  wallets (Balance.plafond here is a display-only placeholder, see loadBalances()), so credit
   *  is 0 — deliberately NOT the fake 999,999, which would make every booking look affordable
   *  and unreachable states unreachable. `null` amount (FX unavailable, §4) is never sufficient. */
  fundsCheck(amount: number | null | undefined): { available: number; sufficient: boolean; short: number } {
    const available = this.balance()?.amount ?? 0;
    if (amount == null) return { available, sufficient: false, short: 0 };
    return { available, sufficient: amount <= available, short: Math.max(0, amount - available) };
  }
  /** The caller's agency UUID (resolved from getMyAgency) — used to attribute bookings (#14).
   *  Always "my own agency", regardless of which agency's wallet is currently being viewed. */
  readonly agencyId = signal('');
  /** The caller's agency display name (resolved from getMyAgency) — real identity for AuthService. */
  readonly agencyName = signal('');

  /** Set when drilling into a sub-agency's wallet from the Agencies page (empty = viewing
   *  my own wallet). Distinct from agencyId/agencyName above, which must keep meaning
   *  "my own agency" for booking attribution and the header identity — see hotel-sourcing
   *  and auth services. */
  readonly viewedAgencyId = signal('');
  readonly viewedAgencyName = signal('');
  readonly viewingOther = computed(() => !!this.viewedAgencyId());
  private readonly effectiveAgencyId = computed(() => this.viewedAgencyId() || this.agencyId());

  constructor() {
    this.agencyApi.getMyAgency().subscribe({
      next: a => { this.agencyId.set(a.id ?? ''); this.agencyName.set(a.name ?? ''); this.loadBalances(); this.loadTopUps(); },
      error: () => this.loadBalances(),
    });
  }

  /** Switch the Wallet page to a sub-agency's balances/ledger (Agencies page drill-in). */
  viewAgency(id: string, name: string): void {
    if (id === this.viewedAgencyId()) return;
    this.viewedAgencyId.set(id);
    this.viewedAgencyName.set(name);
    this.loadBalances();
    this.loadTopUps();
  }

  /** Return the Wallet page to the caller's own agency. */
  viewOwnAgency(): void {
    if (!this.viewedAgencyId()) return;
    this.viewedAgencyId.set('');
    this.viewedAgencyName.set('');
    this.loadBalances();
    this.loadTopUps();
  }

  private loadBalances(): void {
    const id = this.effectiveAgencyId();
    const source = id ? this.agencyApi.getBalancesByAgencyId(id) : this.agencyApi.getBalances();
    source.subscribe({
      next: bs => {
        const mapped: Balance[] = bs.map(b => ({
          id: b.id ?? '',
          currency: b.currency?.abbreviation ?? 'EUR',
          amount: b.amount ?? 0,
          plafond: 999_999, // BE has no credit-limit concept; large value keeps health at 'ok'
        }));
        this.balances.set(mapped);
        if (!readStoredCurrency() && mapped.length) {
          // Default to the funded currency (largest balance) rather than the first
          // one alphabetically — otherwise the header shows e.g. "0.00 CAD" while
          // the real funds sit in EUR. Fall back to EUR, then the first balance.
          const funded = mapped.reduce((a, b) => (b.amount > a.amount ? b : a), mapped[0]);
          const preferred = funded.amount > 0
            ? funded
            : (mapped.find(b => b.currency === 'EUR') ?? mapped[0]);
          this.currency.set(preferred.currency);
        }
        // §1: if the selected currency's wallet ran dry (or the persisted selection doesn't
        // exist in this identity's wallets), fall back to the first payable wallet — the
        // selection must always be a currency the agency can actually pay from.
        const payable = mapped.filter(b => b.amount > 0);
        if (payable.length && !payable.some(b => b.currency === this.currency())) {
          this.currency.set(payable[0].currency);
        }
      },
      error: () => this.balances.set([]),
    });
  }

  private loadTopUps(): void {
    const id = this.effectiveAgencyId();
    if (!id) return;
    this.agencyApi.listTopUps(id, this.currency()).subscribe({
      next: ts => { this.topUps.set(ts.map(mapTopUp)); this.checkPendingCardTopUps(); },
      error: () => {},
    });
  }

  /** Submit a bank-transfer top-up request (PENDING until a manager approves it). Returns
   *  whether it succeeded so the caller (the dialog) can stay open and show an error on
   *  failure instead of closing unconditionally as if it had. */
  requestTopUp(currency: string, amount: number, description: string): Observable<boolean> {
    const id = this.effectiveAgencyId();
    if (!id) return of(false);
    return this.agencyApi.requestTopUp(id, currency, amount, description).pipe(
      map(t => { this.topUps.update(list => [mapTopUp(t), ...list]); return true; }),
      catchError(err => { console.error('[Wallet] requestTopUp failed:', err); return of(false); }),
    );
  }

  /** Re-read the viewed agency's balances. Needed by callers that change money through an API
   *  other than this service's own — editing a transaction goes straight to
   *  PUT /transactions/{id}, and be-agency-service recomputes the balance from the ledger on the
   *  way out, so the amount on screen is stale the moment the edit succeeds. */
  refreshBalances(): void { this.loadBalances(); }

  /** Head-agency funding of the wallet currently being viewed (#2 of the
   *  hotel-agency-management handoff): credits a sub-agency's wallet straight away.
   *
   *  Implemented as create-then-approve rather than a single call, because that is the pair
   *  that is known to work: a bare CHARGE lands PENDING and does not move the balance
   *  (be-agency-service only counts COMPLETED rows), and the PUT that flips it to COMPLETED is
   *  the same @Transactional path that already refreshes the balance for manager approvals.
   *
   *  KNOWN GAP: this credits the sub-agency but does NOT debit the head agency's wallet.
   *  be-agency-service exposes no inter-wallet transfer, so the money appears from nowhere in
   *  ledger terms. The handoff asks for an atomic two-sided transfer — see the gap list. */
  fundAgencyWallet(currency: string, amount: number, description: string): Observable<boolean> {
    const id = this.effectiveAgencyId();
    if (!id || !(amount > 0)) return of(false);
    return this.agencyApi.requestTopUp(id, currency, amount, description).pipe(
      switchMap(created => this.agencyApi.approveTopUp(
        { transactionId: created.transactionId, currency: created.currency ?? currency, amount: created.amount ?? amount, description },
        id,
      )),
      map(t => { this.topUps.update(list => [mapTopUp(t), ...list]); this.loadBalances(); return true; }),
      catchError(err => { console.error('[Wallet] fundAgencyWallet failed:', err); return of(false); }),
    );
  }

  /** Approving/rejecting a sub-agency's top-up while viewing its wallet is the intended
   *  flow: a root manager drills into the sub-agency, reviews, approves/rejects there. */
  approveTopUp(id: string): Observable<boolean> {
    const existing = this.topUps().find(x => x.id === id);
    const agencyId = this.effectiveAgencyId();
    if (!existing || !agencyId) return of(false);
    return this.agencyApi.approveTopUp(
      { transactionId: id, currency: existing.currency, amount: existing.amount, description: existing.description },
      agencyId,
    ).pipe(
      map(t => { this.topUps.update(list => list.map(x => x.id === id ? mapTopUp(t) : x)); this.loadBalances(); return true; }),
      catchError(err => { console.error('[Wallet] approveTopUp failed:', err); return of(false); }),
    );
  }

  rejectTopUp(id: string, note: string): Observable<boolean> {
    const existing = this.topUps().find(x => x.id === id);
    const agencyId = this.effectiveAgencyId();
    if (!existing || !agencyId) return of(false);
    return this.agencyApi.rejectTopUp(
      { transactionId: id, currency: existing.currency, amount: existing.amount, description: existing.description },
      agencyId,
      note,
    ).pipe(
      map(t => { this.topUps.update(list => list.map(x => x.id === id ? mapTopUp(t) : x)); return true; }),
      catchError(err => { console.error('[Wallet] rejectTopUp failed:', err); return of(false); }),
    );
  }

  /** Add a new zero-balance wallet in a currency the agency doesn't have yet. */
  addCurrencyBalance(currency: string): Observable<boolean> {
    const id = this.effectiveAgencyId();
    if (!id) return of(false);
    return this.agencyApi.addBalance(id, currency).pipe(
      map(b => {
        this.balances.update(list => [...list, {
          id: b.id ?? '', currency: b.currency?.abbreviation ?? currency, amount: b.amount ?? 0, plafond: 999_999,
        }]);
        return true;
      }),
      catchError(err => { console.error('[Wallet] addCurrencyBalance failed:', err); return of(false); }),
    );
  }

  readonly balance = computed<Balance | null>(() =>
    this.balances().find(b => b.currency === this.currency()) ?? this.balances()[0] ?? null);
  readonly health = computed<'ok' | 'low' | 'over'>(() => {
    const b = this.balance();
    if (!b) return 'ok';
    if (b.amount <= 0) return 'over';
    return b.amount >= b.plafond * 0.3 ? 'ok' : 'low';
  });

  /** Switch the reservation currency (README §1). Everything priced on screen re-prices in place
   *  — screens watch `currency()` (see search/offer/checkout) — and the ~1.6s `switching` window
   *  plus `repriceKey` drive the coordinated "just switched" feedback. Also re-fetches the top-up
   *  ledger for the newly-selected currency (pre-existing behavior — the ledger always showed
   *  every currency's top-ups mixed together regardless of which wallet was selected). */
  setCurrency(c: string): void {
    this.closePicker();
    if (c === this.currency()) return;
    this.currency.set(c);
    try { sessionStorage.setItem(CCY_STORAGE_KEY, c); } catch { /* storage disabled */ }
    this.repriceKey.update(k => k + 1);
    this.switching.set(c);
    if (this.switchTimer) clearTimeout(this.switchTimer);
    this.switchTimer = setTimeout(() => this.switching.set(null), 1600);
    this.loadTopUps();
  }

  /** Real Credit Card top-up — same Hosted Payment Page (Revolut) flow fe-flight-fusion-web
   *  already uses in production. Unlike the removed local-only stub, this actually persists a
   *  PENDING transaction server-side and opens the hosted page to complete payment. Note:
   *  be-agency-service credits the balance immediately when the charge is created (a pre-existing
   *  BE behavior, not something this FE corrects — see plan notes), so loadBalances() here already
   *  reflects it; no separate optimistic mutation. */
  requestCardTopUp(currency: string, amount: number): Observable<boolean> {
    const id = this.effectiveAgencyId();
    if (!id) return of(false);
    return this.agencyApi.chargeBalanceCard(id, currency, amount, window.location.href).pipe(
      map(t => {
        this.topUps.update(list => [mapChargeTopUp(t), ...list]);
        this.loadBalances();
        if (t.hppUrl) window.open(t.hppUrl, '_blank');
        return true;
      }),
      catchError(err => { console.error('[Wallet] requestCardTopUp failed:', err); return of(false); }),
    );
  }

  /** Settles one PENDING Credit Card row by polling the payment gateway (no webhook exists).
   *  Only the ledger row's status changes here — the balance was already moved when the charge
   *  was created. */
  checkCardTopUpStatus(id: string): void {
    this.agencyApi.getTransactionStatus(id).subscribe({
      next: r => {
        if (r.status && r.status !== 'PENDING') this.loadTopUps();
      },
    });
  }

  /** Called on wallet page load and again when the window regains focus (the natural moment the
   *  user returns from the Revolut tab) — settles any Credit Card top-up still shown PENDING. */
  checkPendingCardTopUps(): void {
    this.topUps()
      .filter(t => t.chargeType === 'CREDIT_CARD' && t.status === 'PENDING')
      .forEach(t => this.checkCardTopUpStatus(t.id));
  }
}

function mapTopUp(t: BeTransactionResponse): WalletTopUp {
  const status: WalletTopUp['status'] =
    t.status === 'COMPLETED' || t.status === 'SUCCESS' ? 'APPROVED' :
    t.status === 'FAILED' || t.status === 'CANCELED' ? 'REJECTED' : 'PENDING';
  return {
    id: t.transactionId ?? '',
    amount: t.amount ?? 0,
    currency: t.currency ?? 'EUR',
    status,
    description: t.description,
    requestedBy: t.createdBy,
    createdAt: t.creationTime ?? t.timeStamp,
    chargeType: t.chargeType,
  };
}

function mapChargeTopUp(t: BeChargeTransactionResponse): WalletTopUp {
  const status: WalletTopUp['status'] =
    t.status === 'COMPLETED' || t.status === 'SUCCESS' ? 'APPROVED' :
    t.status === 'FAILED' || t.status === 'CANCELED' ? 'REJECTED' : 'PENDING';
  return {
    id: t.id ?? '',
    amount: t.amount ?? 0,
    currency: t.currency ?? 'EUR',
    status,
    createdAt: t.timeStamp,
    chargeType: t.chargeType,
  };
}
